<style lang="scss" scoped>
.users {
	&__header {
		@include flex-center;
		&__actions {
			margin-left: auto;
		}
	}
	&__footer {
		@include main-panel-footer;
		&__right {
			margin-left: auto;
		}
	}
	&__container {
		@include container;
	}
	&__panel {
		@include main-panel;
	}
	&__description {
		padding: 20px 40px;
	}
	& ::v-deep .skipped {
		td {
			background: #f9dbdc;
			color: #cc3e3f;
		}
	}
}

.progress-bar {
	margin: 40px 0px 0px 0px;
}
</style>

<template lang="pug">
.users
	app-header
		.users__header(slot="header")
			h1 Match users
			.users__header__actions
				el-button(@click="skipAll") Skip all
				el-button(type="primary", @click="refetch") Refetch predictions
				el-button(type="primary", @click="saveByBatch", :loading="loading") {{ loading ? 'Saving...' : 'Save matches' }}
	.users__container
		.users__panel
			loading(v-if="loading" :message="[`Processed ${processedPredictionCount} of ${predictions.length} predictions`]")
				el-progress.progress-bar(:percentage="progressBarPercentage", :text-inside="true", :stroke-width="18", text-color="#fff")
			loading(v-else-if="$apollo.loading || predictionsLoading")
			
			template(v-else="")
				p.users__description 
					| Below are predicted matches for the data stored in Edusync compared to the users already in your {{ providerName }} account.
					| If applicable, the next set of predictions will load once these have been processed.
				el-table.custom-table.border-top(:row-class-name="tableRowClassName", :data="paginated", style="width: 100%")
					el-table-column(width="200" prop="WondeUser.last_name", sortable="", label="Edusync user")
						template(slot-scope="scope")
							| {{ scope.row.WondeUser.first_name }} {{ scope.row.WondeUser.last_name }}
					el-table-column(width="180" prop="ThirdPartyUser.last_name", sortable="", :label="`${providerName} user`")
						template(slot-scope="scope")
							| {{ scope.row.ThirdPartyUser.first_name }} {{ scope.row.ThirdPartyUser.last_name }}
					el-table-column(prop="ThirdPartyUser.email", sortable="", :label="`${providerName} email`")
					el-table-column(width="140", prop="type", label="Date of birth")
						template(slot-scope="scope")
							| {{ scope.row.WondeUser.date_of_birth | date_convert }}
					el-table-column(width="120", prop="type", label="Type")
						template(slot-scope="scope")
							| {{ scope.row.WondeUser.base_type | capitalize }}
					el-table-column(width="200" prop="score", sortable="", label="Prediction strength")
						template(slot-scope="scope")
							template(v-if="scope.row.skip")
								el-progress(v-if="scope.row.rank === 'confident'", :percentage="100", status="exception")
								el-progress(v-if="scope.row.rank === 'likely'", :percentage="80", status="exception")
								el-progress(v-if="scope.row.rank === 'weak'", :percentage="40", status="exception")
							template(v-else="")
								el-progress(v-if="scope.row.rank === 'confident'", :percentage="100", status="success")
								el-progress(v-if="scope.row.rank === 'likely'", :percentage="80", status="success")
								el-progress(v-if="scope.row.rank === 'weak'", :percentage="40", status="exception")
					el-table-column(width="150", align="center", label="Action")
						template(slot-scope="scope")
							el-button(:type="scope.row.skip ? 'danger' : 'secondary'", @click="skip(scope.row)", size="small") {{ scope.row.skip ? 'Cancel' : 'Skip' }}
				.users__footer(v-if="predictions.length")
					.users__footer__left
						el-pagination(background="", layout="prev, pager, next", :current-page.sync="page", :page-size="per_page", :total="predictions.length")
					.users__footer__right 
						| Showing
						strong &nbsp;{{ paginated.length }}
						| &nbsp;out of a possible
						strong &nbsp;{{ predictions.length }}
						| &nbsp;results
</template>
<script>
import _ from "lodash";
import moment from "moment";
import gql from "graphql-tag";
import Bugsnag from '@bugsnag/js';
export default {
	data() {
		return {
			moment,
			loading: false,
			page: 1,
			per_page: 50,
			predictionsLoading: true,
			processedPredictionCount: 0,
			promises: [],
			hasErrors: false,
		};
	},
	computed: {
		paginated() {
			return _.chunk(this.predictions, this.per_page)[this.page - 1];
		},
		progressBarPercentage() {
			return this.predictions.length == 0 ? 0 : Math.ceil((this.processedPredictionCount / this.predictions.length) * 100);
		}
	},
	apollo: {
		predictions: {
			query: gql`
				{
					Predictions {
						WondeUser {
							id
							first_name
							last_name
							date_of_birth
							email
							base_type
						}
						ThirdPartyUser {
							id
							first_name
							last_name
							email
						}
						rank
					}
				}
			`,
			fetchPolicy: "no-cache",
			update(r) {
				let data = _.cloneDeep(r.Predictions);
				data = _.map(data, (i) => {
					switch (i.rank) {
						case "confident":
							i.score = 1;
							i.skip = false;
							break;
						case "likely":
							i.score = 2;
							i.skip = false;
							break;
						case "weak":
							i.score = 3;
							i.skip = true;
							break;
						default:
							i.skip = true;
							i.score = 4;
							break;
					}
					return i;
				});
				this.predictionsLoading = _.isEmpty(data);
				return data;
			},
			pollInterval: 5000,
			skip() {
				return !this.predictionsLoading;
			}
		},
	},
	methods: {
		async save(predictions) {
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation SavePredictions($input: [PredictionsInput!]!) {
							SavePredictions(input: $input) {
								id
								has_completed_wizard
							}
						}
					`,
					variables: {
						input: _.map(predictions, (r) => {
							return {
								idpId: r.WondeUser.id,
								spId: r.ThirdPartyUser.id,
								action: r.skip == true ? "unmatch" : "match",
							};
						}),
					},
				});
				this.processedPredictionCount = this.processedPredictionCount + predictions.length;
			} catch (error) {
				Bugsnag.notify(error);
				this.hasErrors = true;
			}
		},
		async saveByBatch() {
			this.loading = true;
			this.hasErrors = false;
			this.processedPredictionCount = 0;
			const perPage = 30;
			let curPage = 1;
			let chunk = _.chunk(this.predictions, perPage)[curPage - 1];
			while(chunk) {
				this.promises.push(this.save(chunk));
				curPage++;
				chunk = _.chunk(this.predictions, perPage)[curPage - 1];
			}

			await Promise.all(this.promises);
			this.$root.$emit("directory-reload-required");

			if (this.hasErrors) {
				this.$message.error("Some predictions failed to save. Please re-fetch and try again. Contact support if issue continues");
			} else {
				this.$message.success("Saving predictions successful");
			}
			this.loading = false;
			this.processedPredictionCount = 0;
			await this.refetch();
		},
		skipWeak() {
			_.each(this.predictions, (r) => {
				if (r.rank === "weak") {
					this.$set(r, "skip", true);
				}
			});
		},
		skipAll() {
			_.each(this.predictions, (r) => {
				this.$set(r, "skip", true);
			});
		},
		tableRowClassName({ row }) {
			return row.skip ? "skipped" : "included";
		},
		skip(row) {
			this.$set(row, "skip", !row.skip);
		},
		async refetch() {
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation CacheClear($item: CacheTypeEnum!) {
							CacheClear(item: $item)
						}`,
						variables: {
							item: "predictions"
						}
				});
				this.predictionsLoading = true;
				this.$apollo.queries.predictions.refresh();
			} catch (e) {
				this.$message.error("Failed to re-fetch predictions");
			}
		}
	},
};
</script>